// PST-1: IndexedDB driver via `idb`. DB "tracecad" v1 with the five stores.
// EC-ARC-3: openDriver() falls back to MemoryDriver when IDB is unavailable.
import { openDB, type IDBPDatabase } from "idb";
import { MemoryDriver, type HistoryTail, type PersistDriver, type ProjectMeta } from "./driver";

const DB_NAME = "tracecad";
const STORES = ["projects", "documents", "history", "settings", "assets"] as const;

class IdbDriver implements PersistDriver {
  readonly persistent = true;

  constructor(private db: IDBPDatabase) {}

  getDoc(id: string) {
    return this.db.get("documents", id);
  }
  async putDoc(id: string, json: unknown) {
    await this.db.put("documents", json, id);
  }
  async deleteDoc(id: string) {
    await this.db.delete("documents", id);
  }
  async listMeta(): Promise<ProjectMeta[]> {
    return (await this.db.getAll("projects")) as ProjectMeta[];
  }
  async getMeta(id: string): Promise<ProjectMeta | undefined> {
    return (await this.db.get("projects", id)) as ProjectMeta | undefined;
  }
  async putMeta(meta: ProjectMeta) {
    await this.db.put("projects", meta, meta.id);
  }
  async deleteMeta(id: string) {
    await this.db.delete("projects", id);
  }
  async getHistory(id: string): Promise<HistoryTail | undefined> {
    return (await this.db.get("history", id)) as HistoryTail | undefined;
  }
  async putHistory(id: string, tail: HistoryTail) {
    await this.db.put("history", tail, id);
  }
  async deleteHistory(id: string) {
    await this.db.delete("history", id);
  }
  async getSetting<T>(key: string): Promise<T | undefined> {
    return (await this.db.get("settings", key)) as T | undefined;
  }
  async putSetting(key: string, value: unknown) {
    await this.db.put("settings", value, key);
  }
  async putAsset(hash: string, blob: Blob) {
    await this.db.put("assets", blob, hash);
  }
  async getAsset(hash: string): Promise<Blob | undefined> {
    return (await this.db.get("assets", hash)) as Blob | undefined;
  }
  async deleteAsset(hash: string) {
    await this.db.delete("assets", hash);
  }
  async listAssetHashes(): Promise<string[]> {
    return (await this.db.getAllKeys("assets")) as string[];
  }
}

/** EC-ARC-3: returns the IDB driver, or MemoryDriver when IndexedDB can't open. */
export async function openDriver(): Promise<PersistDriver> {
  try {
    const db = await openDB(DB_NAME, 1, {
      upgrade(db) {
        for (const name of STORES) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      }
    });
    return new IdbDriver(db);
  } catch (err) {
    console.warn("tracecad: IndexedDB unavailable, running in-memory (EC-ARC-3)", err);
    return new MemoryDriver();
  }
}
